import React, { useState, useEffect, FC, useRef } from "react";
import { Icon } from "../../components/Component";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button } from "reactstrap";
import { Modal } from "antd";
import dayjs from "dayjs";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface MaterialIssuePDFProps {
  materialIssueData: any;
  buyerOrgLogo?: string;
  getPdfUrl?: (blob: Blob) => void;
}

const MaterialIssuePDF: FC<MaterialIssuePDFProps> = ({
  materialIssueData,
  buyerOrgLogo,
  getPdfUrl,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;
    const boxX = margin;
    const boxY = 20;
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41;
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };

  const drawSignatureBox = (doc: jsPDF, startY: number) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxWidth = 60;
    const boxHeight = 25;
    const x = pageWidth - boxWidth - 10;
    const y = startY + 10;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, boxWidth, boxHeight);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Authorised Signature",
      x + boxWidth / 2,
      y + boxHeight - 5,
      { align: "center" }
    );
  };

  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;
          const height = Math.min(img.height, maxHeight);
          const width = height * aspectRatio;
          doc.addImage(img.src, "PNG", x, y, width, height);
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          reject(new Error("Image load failed"));
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10);
      } else {
        doc.setFontSize(20);
        doc.text("Procurex", 10, 20);
      }
    };

    const addTable = (doc: any, materialIssueData: any, currentY: any) => {
      const header = materialIssueData?.header;
      const lines = materialIssueData?.lines || [];

      // Define columns with proper headers
      const columns = [
        { title: "Sno", dataKey: "sno" },
        { title: "Material Category", dataKey: "categoryName" },
        { title: "Material Name", dataKey: "materialName" },
        { title: "UOM", dataKey: "uomName" },
        { title: "Issued Quantity", dataKey: "quantityIssued" },
        { title: "Handover Status", dataKey: "handoverStatus" },
        { title: "Handovered Quantity", dataKey: "quantityHandovered" },
      ];

      // Prepare table rows
      const rows = lines.map((line: any, index: number) => ({
        sno: (index + 1).toString(),
        categoryName: line.categoryName || "-",
        materialName: line.materialName || "-",
        uomName: line.uomName || "-",
        quantityIssued: Number(line.quantityIssued || 0).toFixed(2),
        handoverStatus: line.handoverStatus || "-",
        quantityHandovered: Number(line.quantityHandovered || 0).toFixed(2),
      }));

      const totalIssued = lines.reduce(
        (sum: number, line: any) => sum + Number(line.quantityIssued || 0),
        0
      ).toFixed(2);

      doc.autoTable({
        columns: columns,
        body: rows,
        startY: currentY,
        pageBreak: "auto",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          lineWidth: 0.1,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fontSize: 8,
          fillColor: [230, 230, 230],
          textColor: 0,
          valign: "middle",
          halign: "center",
          fontStyle: "bold",
        },
        footStyles: {
          fontSize: 8,
          textColor: 0,
          fillColor: false,
          lineWidth: 0,
          halign: "right",
        },
        bodyStyles: {
          valign: "middle",
          halign: "center",
        },
        theme: "grid",
        margin: { top: 5, bottom: 10, left: 5, right: 5 },
        didDrawPage: function (data: any) {
          const finalY = data.cursor.y;
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.text(
            `Total Issued Quantity: ${totalIssued}`,
            10,
            finalY + 10
          );
        },
      });

      const finalY = (doc as any).lastAutoTable?.finalY || currentY + 100;
      return finalY + 15;
    };

    const breakLongText = (text: any, maxLineLength = 30) => {
      if (!text || typeof text !== 'string') return "";
      return text.match(new RegExp(`.{1,${maxLineLength}}`, "g"))?.join("\n") || text;
    };

    const addFooter = async (
      doc: jsPDF,
      pageNum: number,
      totalPages: number
    ) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = materialIssueData?.orgName || "";
      const line2 = materialIssueData?.orgAddress || "";
      const line3 = materialIssueData?.orgCIN || "";
      const line4 = materialIssueData?.orgGSTIN || "";

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      doc.setFontSize(8);
      const pageText = `Page ${pageNum} of ${totalPages}`;
      doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });

      doc.setFontSize(7);
      if (line1) doc.text(line1, pageWidth / 2, startY, { align: "center" });
      if (line2) doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      if (line3) doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, { align: "center" });
      if (line4) doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, { align: "center" });
    };

    const addGeneralDetailsSection = async (
      doc: jsPDF,
      materialIssueData: any
    ) => {
      const header = materialIssueData?.header || {};
      const sectionY = 16;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);

      let currentY = sectionY + 10;

      // Add Site details
      if (header) {
        const lineHeight = 4;

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(header.siteName || "", 10, currentY);
        currentY += 6;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");

        if (header.segmentLevel && header.segmentLevel !== "null") {
          doc.text(`Site Hierarchy: ${header.segmentLevel}`, 10, currentY);
          currentY += 5;
        }

        if (header.siteStructureUnitName) {
          const towerLabel = header.segmentLevel === "Flat" || header.segmentLevel === "Floor"
            ? "Tower / Block:"
            : "Unit:";
          doc.text(`${towerLabel} ${header.siteStructureUnitName}`, 10, currentY);
          currentY += 5;
        }

        if (header.floorNo) {
          doc.text(`Floor: ${header.floorNo}`, 10, currentY);
          currentY += 5;
        }

        if (header.siteStructureSubUnitName) {
          doc.text(`Flat: ${header.siteStructureSubUnitName}`, 10, currentY);
          currentY += 5;
        }

        if (header.locationName) {
          doc.text(`Location: ${header.locationName}`, 10, currentY);
          currentY += 5;
        }

        currentY += 5;
      }

      // Add "MATERIAL ISSUE" text on the right
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const materialIssueText = "MATERIAL ISSUE";
      const textWidth = doc.getTextWidth(materialIssueText);
      const rightX = pageWidth - textWidth - 10;
      doc.setTextColor(20, 20, 20);
      doc.text(materialIssueText, rightX, sectionY + 30);
      currentY += 5;

      // Draw horizontal line
      doc.setDrawColor(150, 150, 150);
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 10;

      // Add Issue details tables
      const sectionWidth = (pageWidth - 10) / 2;
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;
      const tableStartY = currentY;

      // Left table - Issue details
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          ["Issue Number:", header.issueNumber || " "],
          ["Issue Date:", header.issueDate ? dayjs(header.issueDate).format("DD-MMM-YYYY") : " "],
          ["Issue Status:", header.issueStatus || " "],
          ["Handover Status:", header.handoverStatus || " "],
          ["Actual Status:", header.actualStatus || " "],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      // Right table - Creation details
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          ["Creation Date:", header.createdAt ? dayjs(header.createdAt).format("DD-MMM-YYYY") : " "],
          ["Created By:", header.createdUserName || " "],
          ["Is Chargeable:",
            header.isChargeable === undefined || header.isChargeable === null
              ? "NA"
              : header.isChargeable
                ? "Yes"
                : "No"
          ],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      const maxHeight = Math.max(leftTableHeight, rightTableHeight);
      doc.setDrawColor(150, 150, 150);
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5);

      return maxHeight + 10;
    };

    const addApprovalsSection = async (doc: jsPDF, approvals: any[]) => {
      if (!approvals || approvals.length === 0) {
        return;
      }

      doc.addPage();
      const pageWidth = doc.internal.pageSize.getWidth();
      const borderY = 20;
      let currentY = borderY + 6;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Approvals", pageWidth / 2, currentY, { align: "center" });

      currentY += 8;

      const formatDateTime = (dateString: string | null) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          return formattedDate;
        } catch (error) {
          return "";
        }
      };

      const approvalRowsData = await Promise.all(
        approvals.map(async (approval: any) => {
          let statusText = "";
          const status = (approval.status || "").toUpperCase();
          if (status === "PENDING") {
            statusText = "Pending";
          } else if (status === "APPROVED") {
            const approvedDateTime = formatDateTime(approval.approvalDate || approval.approvedAt);
            statusText = approvedDateTime ? `Approved\n${approvedDateTime}` : "Approved";
          } else if (status === "REJECTED") {
            const rejectedDateTime = formatDateTime(approval.approvalDate);
            statusText = rejectedDateTime ? `Rejected\n${rejectedDateTime}` : "Rejected";
          } else if (status === "SKIPPED") {
            statusText = "Skipped";
          } else {
            statusText = approval.status || "";
          }

          let signatureImage: string | null = null;
          let signaturePath = null;

          if (Array.isArray(approval.dms) && approval.dms.length > 0) {
            signaturePath = approval.dms[0]?.filePath;
          } else if (approval.dms?.filePath) {
            signaturePath = approval.dms.filePath;
          }

          if (signaturePath) {
            try {
              signatureImage = await fetchImageAsBase64(signaturePath);
            } catch (error) {
              console.error("Error fetching signature image:", error);
              signatureImage = null;
            }
          }

          return {
            name: approval.userName || "",
            role: approval.roleName || "",
            level: approval.level || "",
            status: statusText,
            signature: signatureImage,
          };
        })
      );

      const approvalRows = approvalRowsData.map((row: any) => [
        row.name,
        row.role,
        row.level,
        row.status,
        row.signature ? "" : "",
      ]);

      const availableWidth = pageWidth - 20;

      doc.autoTable({
        head: [["Name", "Role", "Level", "Status", "Signature"]],
        body: approvalRows,
        startY: currentY,
        margin: { top: 0, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        headStyles: {
          fontSize: 10,
          fillColor: [230, 230, 230],
          textColor: 0,
          valign: "middle",
          halign: "center",
        },
        styles: {
          fontSize: 9,
          lineWidth: 0.2,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: availableWidth * 0.2, halign: "left" },
          1: { cellWidth: availableWidth * 0.2, halign: "left" },
          2: { cellWidth: availableWidth * 0.12, halign: "left" },
          3: { cellWidth: availableWidth * 0.28, halign: "left" },
          4: { cellWidth: availableWidth * 0.2, halign: "center" },
        },
        didDrawCell: (data: any) => {
          if (data.column.index === 4 && data.section === "body") {
            const rowIndex = data.row.index;
            const signatureData = approvalRowsData[rowIndex];
            if (signatureData?.signature) {
              try {
                const cellPadding = 4;
                const imgWidth = Math.min(35, data.cell.width - cellPadding * 2);
                const imgHeight = Math.min(15, data.cell.height - cellPadding * 2);
                const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                const y = data.cell.y + cellPadding + (data.cell.height - cellPadding * 2 - imgHeight) / 2;
                doc.addImage(signatureData.signature, "PNG", x, y, imgWidth, imgHeight);
              } catch (error) {
                console.error("Error adding signature image:", error);
              }
            }
          }
        },
      });
    };

    // Generate PDF
    let leftLogo: string | null = null;

    if (buyerOrgLogo) {
      try {
        leftLogo = await fetchImageAsBase64(buyerOrgLogo);
      } catch (error) {
        console.error("Buyer logo load failed", error);
        leftLogo = null;
      }
    }

    const currentY = await addGeneralDetailsSection(doc, materialIssueData);

    const tableEndY = await addTable(doc, materialIssueData, currentY);

    // Draw signature box after table
    drawSignatureBox(doc, tableEndY);

    if (materialIssueData?.approvals && materialIssueData.approvals.length > 0) {
      await addApprovalsSection(doc, materialIssueData.approvals);
    }

    const applyHeaderAndFooter = async () => {
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc, i, totalPages);
        drawBorderedBox(doc);
      }
    };

    await applyHeaderAndFooter();

    const finalPageCount = (doc as any).internal.getNumberOfPages();
    if (finalPageCount > 0) {
      doc.setPage(1);
    }

    if (download) {
      try {
        const pdfBlob = doc.output("blob");
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `MI_${materialIssueData?.header?.issueNumber || "MaterialIssue"}.pdf`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error during PDF download:", error);
        try {
          doc.save(`MI_${materialIssueData?.header?.issueNumber || "MaterialIssue"}.pdf`);
        } catch (saveError) {
          console.error("Error with doc.save():", saveError);
          alert("Failed to download PDF. Please try again.");
        }
      }
      return;
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return { pdfUrl, pdfBlob };
  };

  // Call generatePDF on mount to get the blob reference
  useEffect(() => {
    let isMounted = true;

    const initPDF = async () => {
      try {
        const result = await generatePDF();

        if (isMounted && result?.pdfBlob && getPdfUrl) {
          getPdfUrl(result.pdfBlob);
        }
      } catch (error) {
        console.log(error);
      }
    };

    initPDF();

    return () => {
      isMounted = false;
    };
  }, [materialIssueData]);

  const downloadPDF = async () => {
    try {
      await generatePDF(true);
    } catch (error) {
      console.error("Error in downloadPDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  return (
    <>
      <Button
        type="button"
        color=""
        onClick={async () => {
          const { pdfUrl: _pdfUrl }: any = await generatePDF();
          setPdfUrl(_pdfUrl);
        }}
      >
        <Icon name="file-pdf" style={{ marginRight: 1 }} />
        <span>PDF</span>
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Material Issue</span>
              <span
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadPDF().catch((error) => {
                    console.error("Download error:", error);
                  });
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            referrerPolicy="no-referrer"
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default MaterialIssuePDF;